import { useState } from 'react';
import type { DataEntryComponent, DataEntryStoredData } from '../../../../api/pages-client/pages-client.type';
import { initializeRowData, validateRowData } from './data-entry-table.helpers';

/**
 * Custom hook for managing data entry table operations including adding, editing, and deleting rows.
 *
 * @param {DataEntryComponent} component - The data entry component containing column definitions and stored data
 * @param {(component: DataEntryComponent) => void} onEdit - Callback function to handle component updates when data changes
 *
 * @returns {Object} An object containing:
 *   - editingRowIndex: Index of the currently editing row (null if none)
 *   - isAddingRow: Boolean indicating if a new row is being added
 *   - newRowData: Data object for the new row being added
 *   - editRowData: Data object for the row being edited
 *   - saveError: Error message for save operations
 *   - editError: Error message for edit operations
 *   - deleteConfirmation: Object containing delete confirmation modal state
 *   - setNewRowData: Function to update new row data
 *   - setEditRowData: Function to update edit row data
 *   - handleAddRow: Function to initiate adding a new row
 *   - handleSaveNewRow: Function to save a new row with validation
 *   - handleCancelNewRow: Function to cancel adding a new row
 *   - handleEditRow: Function to start editing an existing row
 *   - handleSaveEditRow: Function to save changes to an edited row
 *   - handleCancelEditRow: Function to cancel editing a row
 *   - handleDeleteRow: Function to initiate row deletion with confirmation
 *   - handleConfirmDelete: Function to confirm and execute row deletion
 *   - handleCancelDelete: Function to cancel row deletion
 *
 * @description
 * This hook manages all the state and operations for a data entry table, including:
 * - Row addition with validation using initializeRowData and validateRowData helpers
 * - Row editing with temporary state management
 * - Row deletion with confirmation modal
 * - Error handling for save and edit operations
 * - State management for UI interactions (adding, editing, deleting)
 */

export const useDataEntryTable = (component: DataEntryComponent, onEdit: (component: DataEntryComponent) => void) => {
  const [editingRowIndex, setEditingRowIndex] = useState<number | null>(null);
  const [isAddingRow, setIsAddingRow] = useState(false);
  const [newRowData, setNewRowData] = useState<DataEntryStoredData>({});
  const [editRowData, setEditRowData] = useState<DataEntryStoredData>({});
  const [saveError, setSaveError] = useState<string>('');
  const [editError, setEditError] = useState<string>('');
  const [deleteConfirmation, setDeleteConfirmation] = useState<{
    isOpen: boolean;
    rowIndex: number;
  }>({
    isOpen: false,
    rowIndex: -1,
  });

  const handleAddRow = () => {
    setIsAddingRow(true);
    setNewRowData(initializeRowData(component));
  };

  const handleSaveNewRow = () => {
    const validation = validateRowData(newRowData, component);

    if (!validation.isValid) {
      setSaveError(validation.errorMessage);
      return;
    }

    const updatedComponent = {
      ...component,
      stored_data: [...(component.stored_data || []), newRowData],
    };
    onEdit(updatedComponent);
    setIsAddingRow(false);
    setNewRowData({});
    setSaveError('');
  };

  const handleCancelNewRow = () => {
    setIsAddingRow(false);
    setNewRowData({});
    setSaveError('');
  };

  const handleEditRow = (index: number) => {
    setEditingRowIndex(index);
    const row = component.stored_data?.[index];
    if (!row) return;
    setEditRowData({ ...row });
  };

  const handleSaveEditRow = () => {
    if (editingRowIndex !== null) {
      const validation = validateRowData(editRowData, component);

      if (!validation.isValid) {
        setEditError(validation.errorMessage);
        return;
      }

      const updatedStoredData = [...(component.stored_data || [])];
      updatedStoredData[editingRowIndex] = editRowData;
      const updatedComponent = {
        ...component,
        stored_data: updatedStoredData,
      };
      onEdit(updatedComponent);
      setEditingRowIndex(null);
      setEditRowData({});
      setEditError('');
    }
  };

  const handleCancelEditRow = () => {
    setEditingRowIndex(null);
    setEditRowData({});
    setEditError('');
  };

  const handleDeleteRow = (index: number) => {
    setDeleteConfirmation({
      isOpen: true,
      rowIndex: index,
    });
  };

  const handleConfirmDelete = () => {
    const updatedStoredData = (component.stored_data || []).filter((_, i) => i !== deleteConfirmation.rowIndex);
    const updatedComponent = {
      ...component,
      stored_data: updatedStoredData,
    };
    onEdit(updatedComponent);
    setDeleteConfirmation({
      isOpen: false,
      rowIndex: -1,
    });
  };

  const handleCancelDelete = () => {
    setDeleteConfirmation({
      isOpen: false,
      rowIndex: -1,
    });
  };

  return {
    // State
    editingRowIndex,
    isAddingRow,
    newRowData,
    editRowData,
    saveError,
    editError,
    deleteConfirmation,
    // State setters
    setNewRowData,
    setEditRowData,
    // Handlers
    handleAddRow,
    handleSaveNewRow,
    handleCancelNewRow,
    handleEditRow,
    handleSaveEditRow,
    handleCancelEditRow,
    handleDeleteRow,
    handleConfirmDelete,
    handleCancelDelete,
  };
};
